import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Modal, Row } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { withTranslation } from 'react-i18next';

import { connect } from "../../../../StateProvider";
import { resolveIframeUrl, UNRESOLVED_MARKER } from '../../../../../utils/iframeTemplate';
import LoadingSpin from '../../../LoadingSpin';
import ButtonClose from '../../ButtonClose';

import styles from './iframe.less';

const DEFAULT_HEIGHT = 450;

// `allow-same-origin` нужен чтобы Google Docs/SPA-приложения могли читать свои
// cookies/localStorage. Без `allow-top-navigation` и `allow-modals` — защита от
// фишинга через top.location и поддельных системных диалогов.
const SANDBOX_ATTR = 'allow-scripts allow-same-origin allow-forms allow-popups';

// Permissions Policy: минимум для нормальной работы видео-плееров. Без
// `encrypted-media` YouTube падает с ошибкой 153 на DRM-видео.
const ALLOW_ATTR = 'encrypted-media; picture-in-picture; fullscreen';

const IFRAME_COMMON_PROPS = {
  sandbox: SANDBOX_ATTR,
  allow: ALLOW_ATTR,
  referrerPolicy: 'strict-origin-when-cross-origin',
  loading: 'lazy',
};

const IframeControl = ({
  value,
  config,
  user,
  params,
  canExpandWidth,
  canExpandWidthAndHeight,
  hasFormula,
  t,
}) => {
  const wrapperRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const configUrl = config && config.get('url');
  const recordId = params && params.recordId;
  const catalogId = params && params.catalogId;

  // Если формула задана — она единственный источник значения. config.url
  // подавляется, пока формула не вычислит результат.
  const savedTemplate = (value && value !== '') ? value : (!hasFormula ? (configUrl || '') : '');

  const resolved = useMemo(
    () => resolveIframeUrl(savedTemplate, { user, recordId, catalogId }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedTemplate, user, recordId, catalogId],
  );

  const isResolvedObject = !!resolved && resolved !== UNRESOLVED_MARKER;
  const resolvedUrl = isResolvedObject ? resolved.url : null;

  useEffect(() => {
    setLoadError(false);
    setLoading(!!resolvedUrl);
    // Если URL стал невалидным пока модалка открыта — закрываем её,
    // иначе при возврате URL она всплыла бы сама.
    if (!resolvedUrl) {
      setFullscreen(false);
    }
  }, [resolvedUrl]);

  // Iframe маунтится при первом появлении в viewport и больше не размонтируется —
  // иначе при каждом скролле обратно содержимое перезагружалось бы (плеер
  // сбрасывал бы позицию, форма теряла бы ввод).
  useEffect(() => {
    if (!wrapperRef.current) return undefined;
    const node = wrapperRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const hasContent = isResolvedObject && !loadError;
  const isEmpty = !resolved;

  const wrapperClass = cn(styles.iframeWrapper, {
    [styles.iframeWrapperFullHeight]: canExpandWidthAndHeight,
    [styles.iframeWrapperExpanded]: canExpandWidth && !canExpandWidthAndHeight,
  });

  const previewClass = cn(styles.iframePreview, {
    [styles.iframePreviewFull]: canExpandWidthAndHeight && hasContent,
    [styles.iframePreviewBare]: canExpandWidth || canExpandWidthAndHeight,
    [styles.iframePreviewEmpty]: isEmpty,
  });

  // Размеры превью:
  //   * один в табе → растягиваем на всю высоту (flex)
  //   * один в секции → размеры из embed-кода, fallback на 100% / DEFAULT_HEIGHT
  //   * везде ещё → фиксированная высота DEFAULT_HEIGHT
  const previewStyle = getPreviewStyle({
    hasContent,
    canExpandWidth,
    canExpandWidthAndHeight,
    embedWidth: isResolvedObject ? resolved.width : null,
    embedHeight: isResolvedObject ? resolved.height : null,
  });

  return (
    <div ref={wrapperRef} className={wrapperClass}>
      <div className={previewClass} style={previewStyle}>
        {renderContent({
          resolved,
          resolvedUrl,
          visible,
          loading,
          loadError,
          setLoading,
          setLoadError,
          setFullscreen,
          t,
        })}
      </div>

      {fullscreen && resolvedUrl && (
        <FullscreenModal
          url={resolvedUrl}
          title={t('fieldTypes.iframe.name')}
          onClose={() => setFullscreen(false)}
        />
      )}
    </div>
  );
};

function getPreviewStyle({ hasContent, canExpandWidth, canExpandWidthAndHeight, embedWidth, embedHeight }) {
  if (!hasContent) return {};
  if (canExpandWidthAndHeight) return {};
  if (canExpandWidth) {
    return {
      // max-width даёт responsive в узких контейнерах, центрирование margin'ами
      // при объявленной width меньше контейнера.
      maxWidth: embedWidth || '100%',
      height: embedHeight || `${DEFAULT_HEIGHT}px`,
      margin: embedWidth && embedWidth !== '100%' ? '0 auto' : undefined,
    };
  }
  return { height: DEFAULT_HEIGHT };
}

function renderContent({
  resolved,
  resolvedUrl,
  visible,
  loading,
  loadError,
  setLoading,
  setLoadError,
  setFullscreen,
  t,
}) {
  if (!resolved) {
    return (
      <Placeholder variant="empty">
        {t('fieldTypes.iframe.empty')}
      </Placeholder>
    );
  }
  if (resolved === UNRESOLVED_MARKER) {
    return (
      <Placeholder iconClass="interface-52" variant="unresolved">
        {t('fieldTypes.iframe.unresolved')}
      </Placeholder>
    );
  }
  if (loadError) {
    return (
      <Placeholder iconClass="interface-52" variant="error">
        {t('fieldTypes.iframe.loadError')}
      </Placeholder>
    );
  }
  if (!visible) {
    return null;
  }
  return (
    <>
      <iframe
        key={resolvedUrl}
        src={resolvedUrl}
        className={styles.iframe}
        title={t('fieldTypes.iframe.name')}
        {...IFRAME_COMMON_PROPS}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setLoadError(true);
        }}
      />
      {loading && (
        <div className={styles.iframeLoader}>
          <LoadingSpin className={styles.iframeLoaderIcon} />
        </div>
      )}
      <div className={styles.iframeActions}>
        <a
          className={styles.iframeActionBtn}
          href={resolvedUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={t('fieldTypes.iframe.openInNewTab')}
        >
          <ExportOutlined />
        </a>
        <button
          type="button"
          className={styles.iframeActionBtn}
          onClick={() => setFullscreen(true)}
          title={t('record.fields.expand')}
        >
          <span className="anticon-icon interface-22" />
        </button>
      </div>
    </>
  );
}

const Placeholder = ({ iconClass, variant, children }) => (
  <div
    className={cn(styles.iframePlaceholder, {
      [styles.iframePlaceholderEmpty]: variant === 'empty',
      [styles.iframePlaceholderUnresolved]: variant === 'unresolved',
      [styles.iframePlaceholderError]: variant === 'error',
    })}
  >
    {iconClass && <span className={cn('anticon-icon', iconClass, styles.iframePlaceholderIcon)} />}
    <span className={styles.iframePlaceholderText}>{children}</span>
  </div>
);

Placeholder.propTypes = {
  iconClass: PropTypes.string,
  variant: PropTypes.oneOf(['empty', 'unresolved', 'error']),
  children: PropTypes.node,
};

const FullscreenModal = ({ url, title, onClose }) => (
  <Modal
    open
    width="auto"
    maskClosable
    closable={false}
    footer={null}
    bodyStyle={{ padding: 0 }}
    wrapClassName="large-modal-bpium"
    onCancel={onClose}
  >
    <div className={styles.iframeFullscreenModal}>
      <Row
        type="flex"
        justify="space-between"
        align="middle"
        className={styles.iframeFullscreenHeader}
      >
        <h2 className={styles.iframeFullscreenTitle}>{title}</h2>
        <ButtonClose large shiftRight onClick={onClose} />
      </Row>
      <iframe
        src={url}
        className={styles.iframeFullscreenIframe}
        title={title}
        {...IFRAME_COMMON_PROPS}
      />
    </div>
  </Modal>
);

FullscreenModal.propTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

IframeControl.propTypes = {
  value: PropTypes.string,
  config: PropTypes.object,
  user: PropTypes.object,
  params: PropTypes.object,
  canExpandWidth: PropTypes.bool,
  canExpandWidthAndHeight: PropTypes.bool,
  hasFormula: PropTypes.bool,
  t: PropTypes.func,
};

const IframeControlWithTranslation = withTranslation()(IframeControl);

export default connect(
  IframeControlWithTranslation,
  { user: ['user'] },
  (props, { user }) => ({ ...props, user }),
);
